/**
 * Client-side value schema helpers (mirrors server/lib/periodizationValueSchema.js).
 */

import * as LucideIcons from 'lucide-react';
import { resolveAttributeValueForWeek } from './periodizationPeriodBuckets';

const LEGACY_VALUE_KIND_SCHEMAS = {
  scale_0_100: {
    schema_type: 'numeric_scale',
    config: {
      storage: 'number',
      numeric: { min: 0, max: 100, step: 1, default: 50 },
      segments: [
        { up_to: 40, color: '#ef4444', label: 'Low' },
        { up_to: 70, color: '#eab308', label: 'Med' },
        { up_to: 100, color: '#22c55e', label: 'High' },
      ],
    },
  },
  count_0_5: {
    schema_type: 'numeric_count',
    config: {
      storage: 'number',
      numeric: { min: 0, max: 5, step: 1, default: 0 },
      segments: [
        { up_to: 2, color: '#22c55e' },
        { up_to: 5, color: '#ef4444' },
      ],
    },
  },
  count_1_7: {
    schema_type: 'numeric_count',
    config: {
      storage: 'number',
      numeric: { min: 1, max: 7, step: 1, default: 4 },
      segments: [
        { up_to: 3, color: '#22c55e' },
        { up_to: 7, color: '#ef4444' },
      ],
    },
  },
  boolean: {
    schema_type: 'boolean',
    config: {
      storage: 'boolean',
      boolean: {
        true_label: 'Yes',
        false_label: 'No',
        true_color: '#22c55e',
        false_color: '#737380',
        true_icon: 'check',
        false_icon: 'x',
      },
    },
  },
};

export const VALUE_KIND_COUNT_RANGES = {
  count_0_5: { min: 0, max: 5, default: 0 },
  count_1_7: { min: 1, max: 7, default: 4 },
};

export function resolveSchemaForType(type) {
  if (type?.value_schema?.config) return type.value_schema;
  const legacy = LEGACY_VALUE_KIND_SCHEMAS[type?.value_kind];
  if (legacy) return { schema_type: legacy.schema_type, config: legacy.config, legacy: true };
  return { schema_type: 'numeric_scale', config: LEGACY_VALUE_KIND_SCHEMAS.scale_0_100.config, legacy: true };
}

function segmentColorForValue(value, segments) {
  if (!segments?.length || !Number.isFinite(value)) return null;
  for (const seg of segments) {
    if (value <= Number(seg.up_to)) return seg.color;
  }
  return segments[segments.length - 1]?.color || null;
}

export function normalizeStoredValue(raw, schemaOrType) {
  const schema = schemaOrType?.config ? schemaOrType : resolveSchemaForType(schemaOrType);
  const cfg = schema.config;
  if (!cfg) return raw;
  if (cfg.storage === 'boolean') return !!raw;
  if (cfg.storage === 'lookup_key') return raw == null ? null : String(raw);
  const num = Number(raw);
  const { min = 0, max = 100, default: def = 50, step = 1 } = cfg.numeric || {};
  if (!Number.isFinite(num)) {
    if (schemaOrType?.type_key === 'acwr_max') return 1.3;
    if (schemaOrType?.type_key === 'acwr_min') return 0.8;
    return def;
  }
  let clamped = Math.min(max, Math.max(min, num));
  if (step >= 1) clamped = Math.round(clamped);
  else clamped = Math.round(clamped / step) * step;
  return Math.round(clamped * 1000) / 1000;
}

export function resolveValuePresentation(value, schemaOrType) {
  const schema = schemaOrType?.config ? schemaOrType : resolveSchemaForType(schemaOrType);
  const cfg = schema.config;
  const empty = { label: '—', shortLabel: '—', numericValue: null, color: null, icon: null, backgroundColor: null };
  if (!cfg) return empty;
  if (value == null && cfg.storage !== 'boolean') return empty;

  if (cfg.storage === 'boolean') {
    const b = cfg.boolean || {};
    const on = !!value;
    return {
      label: on ? b.true_label || 'Yes' : b.false_label || 'No',
      shortLabel: on ? b.true_label || 'Yes' : b.false_label || 'No',
      numericValue: on ? 1 : 0,
      color: on ? b.true_color : b.false_color,
      icon: on ? b.true_icon || 'check' : b.false_icon || 'x',
      backgroundColor: on ? b.true_color : b.false_color,
    };
  }

  if (cfg.storage === 'lookup_key') {
    const opt = cfg.lookup?.options?.find((o) => o.key === String(value));
    if (!opt) return { ...empty, label: value != null ? String(value) : '—' };
    return {
      label: opt.label,
      shortLabel: opt.label,
      numericValue: opt.numeric_value,
      color: opt.color,
      icon: opt.icon,
      backgroundColor: opt.color,
    };
  }

  const num = Number(value);
  const color = segmentColorForValue(num, cfg.segments);
  const seg = cfg.segments?.find((s) => num <= Number(s.up_to));
  return {
    label: seg?.label ? `${seg.label} (${num})` : String(num),
    shortLabel: String(num),
    numericValue: num,
    color,
    icon: null,
    backgroundColor: color,
  };
}

export function formatAttributeDisplay(value, typeOrKind) {
  const type =
    typeof typeOrKind === 'object' && typeOrKind != null
      ? typeOrKind
      : { value_kind: typeOrKind };
  return resolveValuePresentation(value, resolveSchemaForType(type)).label;
}

export function clampCountValue(value, valueKind) {
  const range = VALUE_KIND_COUNT_RANGES[valueKind];
  if (!range) return value;
  const n = parseInt(value, 10);
  if (!Number.isFinite(n)) return range.default;
  return Math.min(range.max, Math.max(range.min, n));
}

export function getNumericRange(schemaOrType) {
  const schema = resolveSchemaForType(schemaOrType);
  const cfg = schema.config;
  if (cfg?.numeric) {
    let def = cfg.numeric.default;
    if (schemaOrType?.type_key === 'acwr_max') def = 1.3;
    if (schemaOrType?.type_key === 'acwr_min') def = 0.8;
    return { ...cfg.numeric, default: def };
  }
  return { min: 0, max: 100, step: 1, default: 50 };
}

export function getCellBackgroundColor(value, type) {
  const pres = resolveValuePresentation(value, type);
  if (!pres.backgroundColor) return undefined;
  return `${pres.backgroundColor}33`;
}

export function getChartSeriesFromBlock(tree, typeKey) {
  if (!tree?.phases) return [];
  const type = tree.assessment_set?.attribute_types?.find((t) => t.type_key === typeKey);
  if (!type) return [];
  const points = [];
  for (const phase of tree.phases) {
    for (const micro of phase.microcycles || []) {
      const resolved = resolveAttributeValueForWeek({
        typeKey,
        enabledBuckets: type.period_buckets,
        phase,
        micro,
      });
      if (resolved.value == null) continue;
      const pres = resolveValuePresentation(resolved.value, type);
      points.push({
        week: micro.week_number_in_block,
        weekStart: micro.week_start,
        value: resolved.value,
        numericValue: pres.numericValue,
        label: pres.label,
        color: pres.color,
      });
    }
  }
  return points;
}

export function LucideIconByName({ name, className }) {
  if (!name) return null;
  const pascal = String(name)
    .split('-')
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join('');
  const Icon = LucideIcons[pascal];
  if (!Icon) return null;
  return <Icon className={className} />;
}

export const LUCIDE_ICON_OPTIONS = [
  'arrow-up', 'arrow-down', 'minus', 'check', 'x', 'circle', 'star', 'zap', 'flame', 'activity',
];
